import Globals, { GlobalVars } from "globals";
import { isAuthed } from "ionicApp";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getLocalStorage, setLocalStorage } from "storage";

const USER_REGISTRATION_VERSION_KEY = "AnkiApp.user.registrationVersion";

interface AnnouncementConfig {
  version: string;
  announcementId: string;
  shouldShow: (userState: UserAnnouncementState) => boolean;
}

interface UserAnnouncementState {
  isLoggedIn: boolean;
  hasSeenVersion: boolean;
}

const ANNOUNCEMENTS_STORAGE_KEY = "AnkiApp.announcements.shown";

/**
 * Hook to manage version-specific announcements
 * Handles showing announcements only once per version for logged-in users
 */
export function useVersionAnnouncements() {
  const [shownAnnouncements, setShownAnnouncements] = useState<Set<string>>(new Set());
  const [isInitialized, setIsInitialized] = useState(false);

  // Load previously shown announcements from storage
  useEffect(() => {
    const loadShownAnnouncements = async () => {
      try {
        const stored = await getLocalStorage(ANNOUNCEMENTS_STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) {
            setShownAnnouncements(new Set(parsed));
          }
        }
      } catch (error) {
        console.warn("Failed to load announcement history:", error);
      } finally {
        setIsInitialized(true);
      }
    };

    loadShownAnnouncements();
  }, []);

  // Save shown announcements to storage
  const saveShownAnnouncements = useCallback(async (announcements: Set<string>) => {
    try {
      const array = Array.from(announcements);
      await setLocalStorage(ANNOUNCEMENTS_STORAGE_KEY, JSON.stringify(array));
    } catch (error) {
      console.warn("Failed to save announcement history:", error);
    }
  }, []);

  // Mark an announcement as shown
  const markAnnouncementShown = useCallback(
    async (announcementKey: string) => {
      setShownAnnouncements((prev) => {
        const updated = new Set(prev);
        updated.add(announcementKey);
        saveShownAnnouncements(updated);
        return updated;
      });
    },
    [saveShownAnnouncements],
  );

  // Check if an announcement should be shown
  const shouldShowAnnouncement = useCallback(
    (config: AnnouncementConfig): boolean => {
      if (!isInitialized) {
        return false;
      }

      const announcementKey = `${config.version}-${config.announcementId}`;

      // Don't show if already shown
      if (shownAnnouncements.has(announcementKey)) {
        return false;
      }

      const userState: UserAnnouncementState = {
        isLoggedIn: isAuthed(),
        hasSeenVersion: shownAnnouncements.has(config.version),
      };

      return config.shouldShow(userState);
    },
    [isInitialized, shownAnnouncements],
  );

  return {
    shouldShowAnnouncement,
    markAnnouncementShown,
    isInitialized,
  };
}

/**
 * Compare semantic versions
 * Returns true if version1 >= version2
 */
function isVersionGreaterOrEqual(version1: string, version2: string): boolean {
  const v1Parts = version1.split(".").map(Number);
  const v2Parts = version2.split(".").map(Number);

  for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
    const v1Part = v1Parts[i] || 0;
    const v2Part = v2Parts[i] || 0;

    if (v1Part > v2Part) return true;
    if (v1Part < v2Part) return false;
  }

  return true; // Equal versions
}

/**
 * Hook specifically for the FSRS announcement
 * Shows to existing logged-in users who haven't seen this version yet
 */
export function useFSRSAnnouncement() {
  const { shouldShowAnnouncement, markAnnouncementShown, isInitialized } =
    useVersionAnnouncements();
  const [registrationVersion, setRegistrationVersion] = useState<string | null>(null);
  const [isRegistrationVersionLoaded, setIsRegistrationVersionLoaded] = useState(false);

  // Session-level tracking to prevent multiple presentations per session
  // Using useRef to ensure it survives re-renders but resets on app restart
  const fsrsAnnouncementShownThisSession = useRef(false);

  // Load registration version from storage
  useEffect(() => {
    const loadRegistrationVersion = async () => {
      try {
        const stored = await getLocalStorage(USER_REGISTRATION_VERSION_KEY);
        setRegistrationVersion(stored ?? null);
      } catch (error) {
        console.warn("Failed to load registration version:", error);
      } finally {
        setIsRegistrationVersionLoaded(true);
      }
    };

    loadRegistrationVersion();
  }, []);

  const fsrsAnnouncementConfig: AnnouncementConfig = useMemo(
    () => ({
      version: Globals.version,
      announcementId: "fsrs-default",
      shouldShow: (userState) => {
        // Don't evaluate until all data is loaded to prevent race conditions
        if (!isRegistrationVersionLoaded || !isInitialized) {
          return false;
        }

        // Don't show during Cypress tests to prevent test failures
        if (Globals.environment === "cypress") {
          return false;
        }

        // Don't show if already shown this session
        if (fsrsAnnouncementShownThisSession.current) {
          return false;
        }

        // Don't show if user just registered in this session
        if (GlobalVars.userJustRegistered) {
          return false;
        }

        // Don't show if user is not logged in
        if (!userState.isLoggedIn) {
          return false;
        }

        // Don't show if user has already seen this version's announcement
        if (userState.hasSeenVersion) {
          return false;
        }

        // Critical condition: Only show to users who registered BEFORE version 10.5.0
        // This ensures new users who register with 10.5.0 or later never see it
        if (registrationVersion) {
          // If user registered with 10.5.0 or later, don't show
          if (isVersionGreaterOrEqual(registrationVersion, "10.5.0")) {
            return false;
          }
        } else {
          // If no registration version stored, this could be a very old user
          // or someone who cleared their storage. For safety, only show if they're
          // currently logged in (indicating they're an existing user, not new)
          // and we're sure they haven't just registered
          if (!userState.isLoggedIn) {
            return false;
          }
        }

        // All conditions met: existing user who registered before 10.5.0
        return true;
      },
    }),
    [registrationVersion, isRegistrationVersionLoaded, isInitialized],
  );

  const shouldShow = shouldShowAnnouncement(fsrsAnnouncementConfig);

  const markShown = useCallback(() => {
    const announcementKey = `${fsrsAnnouncementConfig.version}-${fsrsAnnouncementConfig.announcementId}`;
    markAnnouncementShown(announcementKey);
    fsrsAnnouncementShownThisSession.current = true;
  }, [markAnnouncementShown, fsrsAnnouncementConfig]);

  return {
    shouldShowFSRSAnnouncement: shouldShow,
    markFSRSAnnouncementShown: markShown,
  };
}
